import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import SendIcon from '@mui/icons-material/Send';
import {
  extractApiError,
  registerVolunteer,
  requestVolunteerLink,
} from '../api.js';

export default function Benevoles() {
  const [form, setForm] = useState({
    last_name: '',
    first_name: '',
    email: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [linkEmail, setLinkEmail] = useState('');
  const [linkSending, setLinkSending] = useState(false);
  const [linkMsg, setLinkMsg] = useState(null);
  const [linkError, setLinkError] = useState(null);

  async function handleRegister(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await registerVolunteer({
        last_name: form.last_name.trim(),
        first_name: form.first_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
      });
      setSuccess(data.message || 'Demande enregistrée.');
      setForm({ last_name: '', first_name: '', email: '', phone: '' });
    } catch (err) {
      setError(extractApiError(err, 'Inscription impossible.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRequestLink(e) {
    e.preventDefault();
    const email = linkEmail.trim();
    if (!email) return;
    setLinkSending(true);
    setLinkError(null);
    setLinkMsg(null);
    try {
      const data = await requestVolunteerLink(email);
      setLinkMsg(data.message || 'Si un compte validé existe, un lien a été envoyé.');
    } catch (err) {
      setLinkError(extractApiError(err, 'Envoi impossible.'));
    } finally {
      setLinkSending(false);
    }
  }

  return (
    <Container maxWidth="sm">
      <Stack spacing={3} sx={{ mb: 4 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <GroupOutlinedIcon color="primary" />
            <Typography variant="h4">Bénévoles</Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary">
            Demandez un accès pour participer au week-end des 50 ans (16–18 octobre 2026).
            Après validation, vous recevrez un lien pour remplir le planning.
            Conservez cet email : le même lien reste utilisable tant que votre accès est actif.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          component="form"
          onSubmit={handleRegister}
          sx={{ p: 3, border: 1, borderColor: 'divider' }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Demande d&apos;accès
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Nom"
              required
              fullWidth
              value={form.last_name}
              onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
            />
            <TextField
              label="Prénom"
              required
              fullWidth
              value={form.first_name}
              onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
            />
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <TextField
              label="Téléphone (optionnel)"
              fullWidth
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            <Button type="submit" variant="contained" disabled={submitting} size="large">
              {submitting ? 'Envoi…' : 'Envoyer ma demande'}
            </Button>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          component="form"
          onSubmit={handleRequestLink}
          sx={{ p: 3, border: 1, borderColor: 'divider' }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Renvoyer mon lien
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Déjà validé ? Lien perdu, nouvel appareil, ou accès expiré : indiquez votre email
            pour recevoir un nouveau lien (renouvellement 30 jours).
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={linkEmail}
              onChange={(e) => setLinkEmail(e.target.value)}
            />
            {linkError && <Alert severity="error">{linkError}</Alert>}
            {linkMsg && <Alert severity="success">{linkMsg}</Alert>}
            <Button
              type="submit"
              variant="outlined"
              startIcon={<SendIcon />}
              disabled={linkSending}
            >
              {linkSending ? 'Envoi…' : 'Renvoyer mon lien'}
            </Button>
          </Stack>
        </Paper>

        <Divider />
        <Typography variant="body2" color="text.secondary">
          Déjà connecté ?{' '}
          <Box component={RouterLink} to="/benevoles/planning" sx={{ color: 'primary.main' }}>
            Accéder au planning
          </Box>
        </Typography>
      </Stack>
    </Container>
  );
}
